import i18n, { type Resource } from 'i18next';
import { Component, StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { initReactI18next } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';

import { QueryClientProvider } from '~/providers/QueryClientProvider';
import { preloadRoutesFor } from '~/routes/manifest';
import * as Sentry from '~/services/sentry';
import {
  DOMAIN,
  globalTags,
  REASON,
  reportError,
  reportWarning,
} from '~/services/telemetry';
import { runWhenIdleOrInteraction } from '~/utils/deferThirdParty';
import {
  HYDRATION_ERROR_SIGNATURES,
  isHydrationErrorMessage,
  isToleratedHydrationError,
} from '~/utils/hydrationErrors';
import { isStaleChunkError } from '~/utils/staleChunk';

import App from './App';
import { company } from './company/Company';
import { getFaviconDir } from './company/favicons';
import { CompanyName } from './company/interface';
import Translations from './translations.json';
import TranslationsSaaS from './translations-saas.json';
import { getCookieValue } from './utils/cookies';

if (import.meta.env.REACT_APP_ENV !== 'production') {
  import('eruda').then(({ default: eruda }) => {
    eruda.init();
  });
}

/**
 * Errors that say nothing about this app.
 *
 * Every one of these is raised by something outside the bundle — a browser
 * quirk with no stack in our code, or an extension injected into the page —
 * and none of them is actionable. They cost quota that real failures need.
 */
const IGNORED_ERRORS = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  'The play() request was interrupted',
  'AbortError: The operation was aborted',
];

/** Scripts that are not ours, whose stacks can only ever blame themselves. */
const DENIED_URLS = [
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
];

// The SDK competes with the hero image for the connection, and nothing it does
// is needed before the page paints. Everything reported in the meantime is
// queued by the facade and replayed once the SDK arrives. The page-load
// transaction is the one thing deferral cannot preserve — started after LCP it
// would time a load that already happened — so `instrumentPageLoad` is off
// while navigation transactions and trace propagation to the backend stay.
// Web Vitals and resource timings ride on that page-load transaction, so they
// stop reaching Sentry too; they are measured from CrUX and PageSpeed anyway.
const loadSentry = () =>
  Sentry.load((sentry) => ({
    dsn: 'https://0d0291e4f0fd4f45a654bb68b5a1f336@sentry.jiffy-team.com/25',
    integrations: [
      sentry.browserTracingIntegration({ instrumentPageLoad: false }),
    ],
    environment: import.meta.env.DEV
      ? 'development'
      : import.meta.env.REACT_APP_ENV || 'development',
    // Transactions are billed against the same ingest quota as errors, and a
    // storefront's navigation timings are a trend rather than a per-session
    // record — a twentieth of them describes the trend just as well.
    tracesSampleRate: 0.05,
    maxValueLength: 5000,
    // Was 0, which switches normalization off entirely and sends every extras
    // payload at full depth: whole carts, order bodies, delivery slots. Call
    // sites are bounded by `capExtras`; this is the backstop under them.
    normalizeDepth: 3,
    ignoreErrors: IGNORED_ERRORS,
    denyUrls: DENIED_URLS,
    enabled: import.meta.env.PROD,
    release: import.meta.env.REACT_APP_RELEASE
      ? `frontend-cd@${import.meta.env.REACT_APP_RELEASE}`
      : undefined,
    beforeSend(event) {
      // Company, platform and app version belong on every event, not only on
      // the ones this app reports through the telemetry module. An uncaught
      // exception the SDK captures by itself is exactly the kind worth
      // filtering by tenant, and stamping here is also strictly more complete
      // than stamping at report time: the native app version arrives with the
      // React Native handshake, which can land after the first report.
      event.tags = { ...globalTags(), ...event.tags };
      return event;
    },
    beforeSendTransaction(event) {
      // Transactions go through their own hook, so without this the sampled
      // performance data is the one thing that cannot be split by tenant.
      event.tags = { ...globalTags(), ...event.tags };
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (
        breadcrumb.category === 'xhr' &&
        breadcrumb.data?.url?.includes('yandex')
      ) {
        return null;
      }
      if (breadcrumb.category === 'sentry.transaction') {
        return null;
      }
      return breadcrumb;
    },
  })).then(finishEarlyErrorCapture);

/**
 * Feeds unhandled errors to the facade until the SDK's own global handlers
 * exist.
 *
 * Deferring the SDK also defers the `onerror`/`onunhandledrejection` hooks it
 * installs, which would leave the first seconds of every session — the window
 * this whole change is about — reporting nothing at all. Hydration noise is
 * filtered here for the same reason `HydrationErrorBoundary` suppresses it:
 * this listener is registered at module scope and would otherwise beat that
 * boundary to the flood it exists to swallow. Once the SDK is up its own
 * handlers take over, and the flood is suppressed the way it was before this
 * change — by `HydrationErrorBoundary` calling `preventDefault`.
 */
/**
 * How many errors this window may report before the SDK's own handlers exist.
 *
 * These go straight to the facade, so the session budget in `services/
 * telemetry` — which is what stops a runaway reporter everywhere else — never
 * sees them. A loop that throws on every frame starts before the SDK arrives
 * as readily as after it, and the first few are the ones that describe it.
 */
const MAX_EARLY_ERRORS = 10;
let earlyErrorsReported = 0;

const reportEarlyError = (event: ErrorEvent | PromiseRejectionEvent) => {
  const value =
    'reason' in event ? event.reason : (event.error ?? event.message);
  const message = String((value as Error | undefined)?.message ?? value ?? '');
  if (isHydrationErrorMessage(message)) {
    return;
  }
  if (earlyErrorsReported >= MAX_EARLY_ERRORS) {
    return;
  }
  earlyErrorsReported += 1;
  Sentry.captureException(value);
};

/**
 * Hands global error handling over to the SDK — but only once it is actually
 * up. `load` resolves immediately while a first attempt is still in flight, so
 * an early return here would drop the listeners into a gap with no handlers on
 * either side; and a failed load must keep both the listeners and the
 * visibility retry alive.
 */
function finishEarlyErrorCapture() {
  if (!Sentry.isLoaded()) {
    return;
  }
  window.removeEventListener('error', reportEarlyError);
  window.removeEventListener('unhandledrejection', reportEarlyError);
  document.removeEventListener('visibilitychange', loadSentryOnHide);
}

// A visitor leaving before the hero settles takes the queue with them, so the
// SDK is pulled forward the moment the page is hidden.
function loadSentryOnHide() {
  if (document.visibilityState === 'hidden') {
    loadSentry();
  }
}

window.addEventListener('error', reportEarlyError);
window.addEventListener('unhandledrejection', reportEarlyError);
document.addEventListener('visibilitychange', loadSentryOnHide);

runWhenIdleOrInteraction(loadSentry, 'after-lcp');

// Fraction of recovered hydration mismatches reported to Sentry. We suppress
// the noisy unhandled onerror flood but keep a small, de-duplicated signal so a
// regression in SSR/client render parity stays visible on the dashboard.
const HYDRATION_SAMPLE_RATE = 0.02;

class HydrationErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  private errorLoggerRestored = false;
  private errorsList = HYDRATION_ERROR_SIGNATURES;

  listHasErrors = (errorText: string) => {
    return this.errorsList.some((i) => errorText.includes(i));
  };

  suppressHydrationWarnings() {
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      if (!args.length) {
        return;
      }
      const msg = typeof args[0] === 'string' ? args[0] : '';
      if (this.listHasErrors(msg)) {
        return;
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      console.error = originalConsoleError;
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: !isToleratedHydrationError(error) };
  }

  private restoreLogger: (() => void) | null = null;

  componentDidMount() {
    this.restoreLogger = this.suppressHydrationWarnings();
    this.errorLoggerRestored = true;
    window.addEventListener('error', this.handleGlobalError);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError);
    if (this.restoreLogger) {
      this.restoreLogger();
      this.restoreLogger = null;
    }
  }

  handleGlobalError = (event: ErrorEvent) => {
    const isHydrationError =
      event.error?.message && this.listHasErrors(event.error?.message);

    if (isHydrationError) {
      // Observability lives in the `onRecoverableError` hook below (it carries
      // the component stack). Here we only suppress the noisy unhandled onerror.
      event.preventDefault();
      return true;
    }
  };

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (!error?.message || !this.listHasErrors(error?.message)) {
      // Its own reason: sharing the route boundary's would merge two different
      // boundaries — and two different failure modes — into one issue.
      reportError(DOMAIN.ui, error, {
        reason: REASON.ui.hydrationBoundaryFailed,
        extras: { errorInfo },
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

const getCurrentTranslation = () => {
  const companyName = company.name;
  switch (companyName) {
    case CompanyName.CityDrinks:
      return Translations;
    default:
      return TranslationsSaaS;
  }
};

i18n.use(initReactI18next).init({
  resources: getCurrentTranslation() as Resource,
  lng: getCookieValue('lang') ?? company.config.lang,
  fallbackLng: company.config.lang,
  interpolation: {
    escapeValue: false,
    defaultVariables: {
      companyName: company.config.name,
      companyNameSecondary: company.config.nameSecondary,
      companyEmail: company.config.email,
      ageRestricted: company.config.ageRestricted,
      taxName: company.taxName,
    },
  },
});

const origin = window.location.origin;

const dubaiExpressManifest = {
  name: 'DubaiExpress',
  short_name: 'DubaiExpress',
  start_url: origin,
  background_color: '#000',
  theme_color: '#000',
  display: 'standalone',
  icons: [
    {
      src: `${origin}/favicon-dubaiexpress/apple-touch-icon.png`,
      rel: 'apple-touch-icon',
      sizes: '180x180',
    },
    {
      src: `${origin}/favicon-dubaiexpress/favicon-32x32.png`,
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
    },
    {
      src: `${origin}/favicon-dubaiexpress/favicon-16x16.png`,
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
    },
    {
      src: `${origin}/favicon-dubaiexpress/web-app-manifest-192x192.png`,
      rel: 'icon',
      type: 'image/png',
      sizes: '192x192',
    },
    {
      src: `${origin}/favicon-dubaiexpress/web-app-manifest-512x512.png`,
      rel: 'icon',
      type: 'image/png',
      sizes: '512x512',
    },
  ],
};

const manifests = {
  [CompanyName.CityDrinks]: {
    name: 'CityDrinks',
    short_name: 'CityDrinks',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-citydrinks/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '180x180',
      },
      {
        src: `${origin}/favicon-citydrinks/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-citydrinks/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-citydrinks/web-app-manifest-192x192.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '192x192',
      },
      {
        src: `${origin}/favicon-citydrinks/web-app-manifest-512x512.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '512x512',
      },
    ],
  },
  [CompanyName.DubaiExpress]: dubaiExpressManifest,
  [CompanyName.DubaiExpressCom]: dubaiExpressManifest,
  [CompanyName.Localee]: {
    name: 'Localee',
    short_name: 'Localee',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-localee/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '180x180',
      },
      {
        src: `${origin}/favicon-localee/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-localee/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.CircleK]: {
    name: 'CircleK',
    short_name: 'CircleK',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-circlek/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '192x192',
      },
      {
        src: `${origin}/favicon-circlek/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-circlek/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Vilo]: {
    name: 'Vilo Gelato',
    short_name: 'Vilo Gelato',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-vilo/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '192x192',
      },
      {
        src: `${origin}/favicon-vilo/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-vilo/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Jiffy]: {
    name: 'Jiffy',
    short_name: 'Jiffy',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-jiffy/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '192x192',
      },
      {
        src: `${origin}/favicon-jiffy/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-jiffy/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Kids]: {
    name: 'Our Kids',
    short_name: 'Our Kids',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-ourkids/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '192x192',
      },
      {
        src: `${origin}/favicon-ourkids/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-ourkids/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Electronium]: {
    name: 'Electronium DEMO',
    short_name: 'Electronium',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-electronium/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '180x180',
      },
      {
        src: `${origin}/favicon-electronium/favicon.svg`,
        rel: 'icon',
        type: 'image/svg+xml',
      },
      {
        src: `${origin}/favicon-electronium/favicon-96x96.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '96x96',
      },
      {
        src: `${origin}/favicon-electronium/web-app-manifest-192x192.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '192x192',
      },
      {
        src: `${origin}/favicon-electronium/web-app-manifest-512x512.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '512x512',
      },
    ],
  },
  [CompanyName.Oskuhus]: {
    name: 'Öskuhús',
    short_name: 'Öskuhús',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-oskuhus/apple-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '192x192',
      },
      {
        src: `${origin}/favicon-oskuhus/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-oskuhus/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
    ],
  },
  [CompanyName.Heimkaup]: {
    name: 'Heimkaup',
    short_name: 'Heimkaup',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-heimkaup/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '180x180',
      },
      {
        src: `${origin}/favicon-heimkaup/favicon.svg`,
        rel: 'icon',
        type: 'image/svg+xml',
      },
      {
        src: `${origin}/favicon-heimkaup/favicon-96x96.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '96x96',
      },
      {
        src: `${origin}/favicon-heimkaup/web-app-manifest-192x192.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '192x192',
      },
      {
        src: `${origin}/favicon-heimkaup/web-app-manifest-512x512.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '512x512',
      },
    ],
  },
  [CompanyName.Cerise]: {
    name: 'Cerise',
    short_name: 'Cerise',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-cerise/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '180x180',
      },
      {
        src: `${origin}/favicon-cerise/favicon.svg`,
        rel: 'icon',
        type: 'image/svg+xml',
      },
      {
        src: `${origin}/favicon-cerise/favicon-96x96.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '96x96',
      },
      {
        src: `${origin}/favicon-cerise/web-app-manifest-192x192.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '192x192',
      },
      {
        src: `${origin}/favicon-cerise/web-app-manifest-512x512.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '512x512',
      },
    ],
  },
  [CompanyName.Storrd]: {
    name: 'Storrd Ltd',
    short_name: 'Storrd Ltd',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-storrd/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '1024x1024',
      },
      {
        src: `${origin}/favicon-storrd/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '64x64',
      },
      {
        src: `${origin}/favicon-storrd/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-storrd/web-app-manifest-192x192.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '384x384',
      },
      {
        src: `${origin}/favicon-storrd/web-app-manifest-512x512.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '1024x1024',
      },
    ],
  },
  [CompanyName.MCosmetic]: {
    name: 'MCosmetic',
    short_name: 'MCosmetic',
    start_url: origin,
    background_color: '#000',
    theme_color: '#000',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-mcosmetic/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '1024x1024',
      },
      {
        src: `${origin}/favicon-mcosmetic/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-mcosmetic/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
      {
        src: `${origin}/favicon-mcosmetic/web-app-manifest-192x192.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '192x192',
      },
      {
        src: `${origin}/favicon-mcosmetic/web-app-manifest-512x512.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '512x512',
      },
    ],
  },
  [CompanyName.Ocado]: {
    name: 'Корзинка Фарма',
    short_name: 'Корзинка Фарма',
    start_url: origin,
    background_color: '#ffffff',
    theme_color: '#17a784',
    display: 'standalone',
    icons: [
      {
        src: `${origin}/favicon-ocado/apple-touch-icon.png`,
        rel: 'apple-touch-icon',
        sizes: '180x180',
      },
      {
        src: `${origin}/favicon-ocado/web-app-manifest-192x192.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '192x192',
      },
      {
        src: `${origin}/favicon-ocado/web-app-manifest-512x512.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '512x512',
      },
      {
        src: `${origin}/favicon-ocado/favicon-32x32.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
      },
      {
        src: `${origin}/favicon-ocado/favicon-16x16.png`,
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
      },
    ],
  },
};

if (manifests[company.name as keyof typeof manifests]) {
  const stringManifest = JSON.stringify(
    manifests[company.name as keyof typeof manifests],
  );
  const blob = new Blob([stringManifest], { type: 'application/json' });
  const manifestURL = URL.createObjectURL(blob);
  document
    .querySelector('#manifest-placeholder')
    ?.setAttribute('href', manifestURL);

  let link = document.querySelector(
    "link[rel='shortcut icon']",
  ) as HTMLLinkElement;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    if (document?.getElementsByTagName('head')?.[0]) {
      document.getElementsByTagName('head')[0]!.appendChild(link);
    }
  } else if (manifests[company.name as keyof typeof manifests]) {
    link.href = `/${getFaviconDir(company.name)}/favicon.ico`;
  }
}

// Provider setup pulls in the Firebase SDK and five more vendors. The import is
// dynamic so those bytes are fetched after the critical window, not merely
// executed later: a static import would download and parse them regardless of
// when initAnalytics() runs. Early track() calls stay buffered by
// AnalyticsService and are flushed on initialize.
//
// The phase is `conversion` rather than `after-lcp` for the same reason the
// vendor pageview tags use it: that buffer lives in page memory, so a visitor
// who leaves before the providers arrive contributes nothing at all — and BI
// counts on these events. Leaving the page also pulls the load forward, the
// last chance to flush what is buffered.
let analyticsRequested = false;

const loadAnalytics = () => {
  if (analyticsRequested) {
    return;
  }
  analyticsRequested = true;
  document.removeEventListener('visibilitychange', loadAnalyticsOnHide);

  import('~/services/analytics/init')
    .then(({ initAnalytics }) => {
      initAnalytics();
    })
    .catch((error) => {
      // Unlike lazyRoute, a stale chunk here is not retried with a reload: the
      // user is not looking at a broken route, just a session with no
      // analytics, and forcing a reload mid-session to fix an invisible
      // problem would be a worse regression than the lost events.
      if (isStaleChunkError(error)) {
        return;
      }
      reportError(DOMAIN.chunk, error, {
        reason: REASON.chunk.loadFailed,
        extras: { context: 'analytics providers failed to load' },
      });
    });
};

function loadAnalyticsOnHide() {
  if (document.visibilityState === 'hidden') {
    loadAnalytics();
  }
}

document.addEventListener('visibilitychange', loadAnalyticsOnHide);
runWhenIdleOrInteraction(loadAnalytics, 'conversion');

// Apply design system version and company attributes to body
document.body.setAttribute(
  'data-design',
  company.newDesignSystemEnabled ? 'v3' : 'v2',
);
document.body.setAttribute('data-company', company.name);

/**
 The following code is replacing favicons for the dev env
 with the same icons but different colors.
 It's not used in production.
 */
// if (import.meta.env.DEV) {
//   const favicon = document.querySelectorAll<HTMLLinkElement>('link[rel=icon]');
//   favicon.forEach((link) => {
//     const newHref = link.href.replace(/(\/public\/)(.+)/, '$1development/$2');
//     return (link.href = newHref);
//   });
// }

const hydrate = () =>
  ReactDOM.hydrateRoot(
    document.getElementById('app')!,
    <HydrationErrorBoundary>
      <StrictMode>
        <QueryClientProvider>
          {/* Route changes run as a transition, so React keeps the page the
              user is looking at on screen while the next route's chunk loads
              instead of swapping it for the Suspense fallback. */}
          <BrowserRouter future={{ v7_startTransition: true }}>
            <App globalContext={window.__INITIAL_STATE__?.context} />
          </BrowserRouter>
        </QueryClientProvider>
      </StrictMode>
    </HydrationErrorBoundary>,
    {
      // React 18 calls this for every recovered hydration mismatch, with the
      // component stack of the diverging element. This is the canonical place to
      // observe SSR/client parity regressions. Report a small, de-duplicated
      // sample to Sentry (the flood is otherwise suppressed in handleGlobalError)
      // and, in dev, log the component stack so the exact culprit is visible.
      onRecoverableError: (error, errorInfo) => {
        const message = (error as Error)?.message ?? '';
        const componentStack = errorInfo?.componentStack ?? '';

        if (import.meta.env.DEV) {
          // biome-ignore lint/suspicious/noConsole: dev-only hydration diagnostics
          console.error(
            '[HYDRATION MISMATCH]',
            message,
            '\nCOMPONENT STACK:',
            componentStack,
          );
        }

        // Only report genuine hydration mismatches (not every recoverable error),
        // and split the fingerprint by the failing component so distinct
        // regressions stay as separate Sentry issues instead of collapsing into
        // one that a resolved/muted state could hide.
        if (!isHydrationErrorMessage(message)) {
          return;
        }

        if (Math.random() < HYDRATION_SAMPLE_RATE) {
          const topFrame =
            componentStack
              .split('\n')
              .map((line) => line.trim())
              .find(Boolean) ?? 'unknown';

          reportWarning(DOMAIN.hydration, 'Recovered mismatch', {
            reason: REASON.hydration.recoveredMismatch,
            // The top frame keeps distinct components apart; the React message
            // stays out of the title, where it used to split one regression
            // across an issue per wording.
            fingerprint: [topFrame],
            extras: { message, componentStack },
          });
        }
      },
    },
  );

// The route chunk has to be in memory before hydration. Hydrating a Suspense
// fallback where the server rendered the real page would discard the SSR markup
// and shift the layout. A chunk that fails to load still hydrates — Suspense
// then shows the fallback and React retries the import — and the rejection is
// swallowed here so it does not surface as an unhandled promise rejection on
// top of it.
preloadRoutesFor(window.location.pathname + window.location.search).then(
  hydrate,
  hydrate,
);
